import { useEffect, useState } from "react";
import {
  fetchMovieDetails,
  fetchMovieCredits,
  fetchWatchProviders,
  fetchMovieVideos,
} from "../api/tmdb";
import "./MovieDetailsModal.css";

function MovieDetailsModal({ movieId, onClose }) {
  const [details, setDetails] = useState(null);
  const [cast, setCast] = useState([]);
  const [providers, setProviders] = useState(null);
  const [trailerKey, setTrailerKey] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Lock background scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "auto";
    };
  }, []);

  useEffect(() => {
    if (!movieId) return;

    let mounted = true;
    setLoading(true);

    const loadAll = async () => {
      try {
        const [d, c, p, v] = await Promise.all([
          fetchMovieDetails(movieId),
          fetchMovieCredits(movieId),
          fetchWatchProviders(movieId),
          fetchMovieVideos(movieId),
        ]);

        if (!mounted) return;

        setDetails(d || null);
        setCast(Array.isArray(c) ? c.slice(0, 12) : []);
        setProviders(p || null);

        const trailer =
          v?.results?.find((x) => x.type === "Trailer" && x.site === "YouTube") ||
          v?.results?.find((x) => x.site === "YouTube");

        setTrailerKey(trailer?.key || null);
      } catch (err) {
        console.log("Modal fetch failed:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadAll();
    return () => {
      mounted = false;
    };
  }, [movieId]);

  if (!movieId) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal premium" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>
          ✖
        </button>

        {loading && (
          <div className="modal-loading">
            <div className="spinner" />
            <p>Loading details...</p>
          </div>
        )}

        {!loading && details && (
          <>
            {/* HERO */}
            <div
              className="modal-hero"
              style={{
                backgroundImage: `linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.35)),
                url(https://image.tmdb.org/t/p/original${
                  details.backdrop_path || details.poster_path
                })`,
              }}
            >
              <div className="modal-hero-content">
                <h2 className="modal-title">{details.title}</h2>

                <div className="modal-sub">
                  <span>⭐ {details.vote_average?.toFixed(1)}</span>
                  <span>• {details.release_date?.slice(0, 4)}</span>
                  <span>• {details.original_language?.toUpperCase()}</span>
                </div>

                <div className="genre-chips">
                  {details.genres?.slice(0, 4).map((g) => (
                    <span key={g.id} className="chip">
                      {g.name}
                    </span>
                  ))}
                </div>

                <div className="modal-actions">
                  {trailerKey && (
                    <a
                      className="btn primary"
                      href={`https://www.youtube.com/watch?v=${trailerKey}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      ▶ Play Trailer
                    </a>
                  )}

                  <button className="btn ghost">❤️ Like</button>
                  <button className="btn ghost">🔖 Save</button>
                </div>
              </div>
            </div>

            {/* ✅ BODY SCROLL */}
            <div className="modal-body-scroll">
              <div className="modal-grid">
                {/* LEFT */}
                <div className="modal-left">
                  {trailerKey && (
                    <div className="trailer-box">
                      <iframe
                        src={`https://www.youtube.com/embed/${trailerKey}?autoplay=0&mute=1&controls=1&rel=0`}
                        title="Trailer"
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}

                  <div className="modal-section">
                    <h4>📖 Storyline</h4>
                    <p className="overview">
                      {details.overview || "No overview available."}
                    </p>
                  </div>
                </div>

                {/* RIGHT */}
                <div className="modal-right">
                  {/* CAST */}
                  {cast.length > 0 && (
                    <div className="modal-section">
                      <h4>🎭 Top Cast</h4>
                      <div className="cast-col">
                        {cast.map((actor) => (
                          <div className="cast-card" key={actor.id}>
                            <img
                              src={
                                actor.profile_path
                                  ? `https://image.tmdb.org/t/p/w185${actor.profile_path}`
                                  : "https://via.placeholder.com/80x80?text=No+Img"
                              }
                              alt={actor.name}
                            />
                            <div className="cast-info">
                              <p className="cast-name">{actor.name}</p>
                              <p className="cast-role">{actor.character}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* PROVIDERS */}
                  {providers?.flatrate?.length > 0 && (
                    <div className="modal-section">
                      <h4>📺 Where to Watch</h4>

                      <div className="provider-col">
                        {providers.flatrate.map((p) => (
                          <div className="provider-card" key={p.provider_id}>
                            <img
                              src={`https://image.tmdb.org/t/p/w92${p.logo_path}`}
                              alt={p.provider_name}
                            />
                            <span>{p.provider_name}</span>
                          </div>
                        ))}
                      </div>

                      {providers.link && (
                        <a
                          className="btn primary open-ott"
                          href={providers.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open in OTT →
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {!loading && !details && (
          <div className="modal-section">
            <p>❌ Movie details not found.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default MovieDetailsModal;
